import { reportOnboardingFailure } from "../lib/onboardingTelemetry";
import { readReturnToPostTask } from "../lib/returnToPost";
import { handoffToolUrl, WebsiteHandoffError } from "../lib/websiteHandoff";
import { WebsiteHandoffRecovery } from "./WebsiteHandoffRecovery";
import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { creationIntent, onboardingReturnPath, pricingDestination } from "../lib/onboardingIntent";
import { hasJustCompletedOnboarding, onboardingRequest } from "../lib/onboarding";

/** Existing businesses and invited teammates enter the app normally. */
export function OnboardingGate({ children }: { children: React.ReactNode }) {
  const { currentUser, activeWorkspaceId, activeWorkspace } = useAuth();
  const location = useLocation();
  const [decision, setDecision] = useState<{
    workspace: string;
    userId: string;
    required: boolean;
    entry: string;
    error?: WebsiteHandoffError;
  } | null>(null);
  const params = new URLSearchParams(location.search);
  const entry = `${location.pathname}${location.search}`;
  const planId = params.get("planId");
  const continuingPost =
    (location.pathname === "/create-post" && !!params.get("draftId")) ||
    (location.pathname === "/created" && !!params.get("viewPost")) ||
    (location.pathname === "/ideas" && !!params.get("viewIdea")) ||
    (location.pathname === "/settings" && (!!params.get("draftId") || !!readReturnToPostTask()));
  const exempt = continuingPost || !!planId ||
    location.pathname === "/onboarding" ||
    location.pathname === "/settings/linkedin-pages" ||
    location.pathname === "/settings/facebook-pages" ||
    location.pathname.startsWith("/invite") ||
    location.pathname.startsWith("/documents/") ||
    location.pathname === "/upgrade";
  const sameWorkspace = !!currentUser && decision?.userId === currentUser.uid &&
    decision?.workspace === activeWorkspaceId;
  useEffect(() => {
    let stale = false;
    // Discard the entry decision while setup runs. Its completion changes eligibility.
    if (location.pathname === "/onboarding") {
      setDecision(null);
      return;
    }
    if (exempt) return;
    if (!currentUser || !activeWorkspaceId || activeWorkspace?.role !== "owner")
      return;
    // Eligibility belongs to the workspace, not the current URL. Keep the
    // composer mounted when it consumes and removes handoff parameters.
    if (sameWorkspace && (decision.entry === entry || (!decision.required && !decision.error))) return;
    onboardingRequest(currentUser, activeWorkspaceId)
      .then(async (result) => {
        // Check transient content before asking a new user to complete setup.
        // This is a read-only validation; GeneratePage still owns loading it.
        const incoming = new URLSearchParams(location.search);
        if (location.pathname === "/generate" && incoming.has("pendingContent") && result.eligible) {
          try {
            if (!incoming.get("claimToken")) throw new Error("Missing token");
            const response = await fetch(`${process.env.REACT_APP_API_BASE || "https://api.wavegen.ai"}/api/content/pending/${encodeURIComponent(incoming.get("pendingContent")!)}?claimToken=${encodeURIComponent(incoming.get("claimToken")!)}&validateOnly=true`, {
              headers: { Authorization: `Bearer ${await currentUser.getIdToken()}`, "x-workspace-id": activeWorkspaceId },
            });
            if (!response.ok || (await response.json()).valid !== true) throw new Error("Unavailable content");
          } catch {
            if (!stale) reportOnboardingFailure("pending_content_validation", activeWorkspaceId);
            if (!stale) setDecision({userId: currentUser.uid, workspace: activeWorkspaceId, entry, required: false, error: new WebsiteHandoffError(handoffToolUrl(incoming))});
            return;
          }
        }
        if (!stale)
          setDecision({
            userId: currentUser.uid,
            workspace: activeWorkspaceId,
            entry,
            required: result.eligible,
          });
      })
      .catch(() => {
        // A setup service outage must not lock an established customer out of the app.
        if (!stale)
          setDecision({ userId: currentUser.uid, workspace: activeWorkspaceId, entry, required: false });
      });
    return () => {
      stale = true;
    };
  }, [
    exempt,
    decision,
    sameWorkspace,
    currentUser,
    activeWorkspaceId,
    activeWorkspace?.role,
    location.pathname,
    location.search,
    entry,
  ]);
  if (planId && location.pathname !== "/upgrade" && activeWorkspace?.role === "owner") {
    params.delete("planId");
    const backTo = `${location.pathname}${params.toString() ? `?${params}` : ""}`;
    return <Navigate to={pricingDestination(planId, backTo)} replace />;
  }
  if (exempt || activeWorkspace?.role !== "owner") return <>{children}</>;
  if (location.pathname === "/generate" && params.has("pendingContent") && !params.get("claimToken"))
    return <WebsiteHandoffRecovery error={new WebsiteHandoffError(handoffToolUrl(params))} />;
  if (sameWorkspace && decision?.entry === entry && decision.error)
    return <WebsiteHandoffRecovery error={decision.error} />;
  // Revalidate in the background without flashing a loading page after the
  // completion request has already confirmed this workspace is ready.
  if (currentUser && activeWorkspaceId && !sameWorkspace &&
      hasJustCompletedOnboarding(currentUser, activeWorkspaceId)) return <>{children}</>;
  if (sameWorkspace && decision && !decision.required && !decision.error) return <>{children}</>;
  if (!activeWorkspaceId || !sameWorkspace || !decision || decision.entry !== entry)
    return (
      <div
        role="status"
        className="min-h-screen grid place-items-center text-sm text-muted-foreground"
      >
        Getting your workspace ready…
      </div>
    );
  const resume = onboardingReturnPath(`${location.pathname}${location.search}`);
  const setupParams = new URLSearchParams(location.pathname === "/generate" ? creationIntent(location.search) || "" : "");
  if (resume && location.pathname !== "/generate") setupParams.set("returnTo", resume);
  return decision.required ? (
    <Navigate to={setupParams.toString() ? `/onboarding?${setupParams}` : "/onboarding"} replace />
  ) : (
    <>{children}</>
  );
}
